import Filters from "features/Filters";
import React, {
  memo,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components/macro";
import { Desktop, device } from "theme/Breakpoints";
import { batch, useDispatch, useSelector } from "react-redux";
import {
  selectInitialLoad,
  setActiveFilters,
  setActiveFiltersCount,
} from "features/Filters/filtersSlice";
import { extractActiveFilters } from "features/Filters/utils";
import { useNavigate } from "react-router-dom";
import { setBreadcrumbTitle } from "features/breadcrumb/breadcrumbSlice";
import {
  fetchFilteredProducts,
  selectActiveSortingType,
  selectFilteredProducts,
  selectProductsStatus,
  setFilteredProducts,
} from "features/category/categorySlice";
import ContentGrid from "features/category/ContentGrid";
import ContentTopSection from "features/category/ContentTopSection";
import ProductCard from "components/Product";
import { useSelectFilters } from "./hooks/useSelectFilters";
import { PageWithFilterType } from "features/Filters/types";
import Pagination from "features/pagination";
import {
  selectPage,
  selectTotalItems,
  setPage,
} from "features/pagination/paginationSlice";
import { colors } from "theme/Theme";
import {
  selectIsFooterVisible,
  setFooter,
} from "components/Footer/footerSlice";
import { EventService } from "utils/EventService";
import { getURLSearch, isPending } from "features/utils";

interface CategoryProps {}

const Category: React.FC<CategoryProps> = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryParams = new URLSearchParams(window.location.search);
  const page = useSelector(selectPage);
  const filteredProducts = useSelector(selectFilteredProducts);
  const productsLoading = useSelector(selectProductsStatus);
  const promo_title = queryParams.get("promo_title");
  const totalItems = useSelector(selectTotalItems);
  const activeCategorySortingType = useSelector(selectActiveSortingType);
  const initialLoad = useSelector(selectInitialLoad);
  const [firstRerender, setFirstRerender] = useState<boolean>(false);
  const restoreIdRef = useRef();
  const footerIsVisible = useSelector(selectIsFooterVisible);

  var activeFilters = useMemo(
    () => extractActiveFilters(queryParams),
    [queryParams]
  );

  const { filters, activeFiltersCount } = useSelectFilters(activeFilters);

  useEffect(() => {
    const pageParam = queryParams.get("page");
    if (!pageParam) {
      setFirstRerender(!firstRerender);
      const searchURL = getURLSearch();
      window.history.replaceState(
        null,
        "Категория",
        `?page=${page}${searchURL !== "" ? `&${searchURL}` : ""}`
      );
    } else {
      if (Number(pageParam) !== page) {
        dispatch(setPage(Number(pageParam)));
        return;
      }

      dispatch(
        fetchFilteredProducts(activeFilters, page, activeCategorySortingType)
      );
    }
  }, [
    firstRerender,
    page,
    window.location.pathname,
    window.location.search,
    activeCategorySortingType,
  ]);

  useLayoutEffect(() => {
    if (!productsLoading && filteredProducts.length > 0) {
      dispatch(setFooter(true));

      if (restoreIdRef.current) {
        const item: HTMLDivElement | null = document.querySelector(
          `.restore-${restoreIdRef.current}`
        );
        if (item) {
          window.scrollTo(0, item.offsetTop);
        }
      }
    } else if (footerIsVisible) {
      dispatch(setFooter(false));
    }
  }, [productsLoading, filteredProducts]);

  const reset = () => {
    batch(() => {
      dispatch(setActiveFilters([]));
      dispatch(setActiveFiltersCount(0));
      dispatch(setFilteredProducts([]));
    });
  };

  const handleBackButtonFromDetails = (data: any) => {
    if (data.productId) {
      restoreIdRef.current = data.productId;
    }
  };

  useEffect(() => {
    dispatch(setBreadcrumbTitle("Климатици"));
    EventService.addEventListener(
      "backButtonFromDetails",
      handleBackButtonFromDetails
    );

    return () => {
      EventService.removeEventListener("backButtonFromDetails");
      dispatch(setBreadcrumbTitle(""));
      dispatch(setPage(1));
      reset();
    };
  }, []);

  const removeFilters = () => {
    reset();
    navigate(window.location.pathname);
  };
  const isLoading =
    !initialLoad || isPending(productsLoading) || !productsLoading;

  return (
    <>
      {promo_title && (
        <PromoTitle className="text-lg">{promo_title}</PromoTitle>
      )}
      <CategoryContainer>
        <Desktop>
          <FiltersContainer>
            {initialLoad && (
              <Filters
                filters={filters}
                removeFiltersCB={removeFilters}
                activeFiltersCount={activeFiltersCount}
                pageType={
                  window.location.pathname === "/promotions"
                    ? PageWithFilterType.promotions
                    : PageWithFilterType.category
                }
                fetchFN={fetchFilteredProducts}
              />
            )}
          </FiltersContainer>
        </Desktop>
        <ContentContainer>
          {initialLoad && (
            <ContentTopSection
              itemsLength={
                isLoading && filteredProducts.length === 0 ? 0 : totalItems
              }
            />
          )}
          <ContentGrid
            data={filteredProducts}
            ComponentToRender={ProductCard}
            isLoading={isLoading}
          />
          {initialLoad && <Pagination disabled={isLoading} />}
        </ContentContainer>
      </CategoryContainer>
    </>
  );
};

const CategoryContainer = styled.div`
  box-sizing: border-box;
  width: 100%;
  display: flex;
  flex-flow: row nowrap;
  align-items: flex-start;
  gap: 20px;
  padding: 16px 0 30px;

  @media only screen and (${device.tablet}) {
    padding-top: 30px;
  }
`;

export const FiltersContainer = styled.div`
  display: flex;
  flex: 0 0 290px;
  flex-flow: column;
  width: 290px;
  max-width: 290px;
  padding-left: 16px;
`;

const ContentContainer = styled.div`
  box-sizing: border-box;
  flex: 1 1 auto;
  width: auto;
  min-width: 0;
  padding: 0 12px;

  @media only screen and (${device.tablet}) {
    padding: 0 20px;
  }
`;

const PromoTitle = styled.div`
  color: ${colors.blue};
  text-align: center;
  font-weight: bold;
  padding: 10px 20px;
  @media only screen and (${device.tablet}) {
    padding: 20px 40px;
  }
`;

export default memo(Category);
